import { Component } from 'preact'
import classnames from 'classnames'
import Icon from '../assets'
import BUTTONS_CONFIGURATION from './buttons-configuration.json'
import ColorPicker from './ColorPicker'
import InputGroup from '../../control-components/ui-components/InputGroup'
import Switcher from '../control/Switcher'
import { StylingConfigType } from './configurator'
import Button from '@insided/control/uikit/Button'
import { generateCssClassName } from './generateCssClassName'
import ConfigurationSetting from './ConfigurationSetting'

interface GenericSetting {
  key: string
  label: string
  type: string
}

interface ButtonStatusData {
  background: string
  text: string
  border: string
  borderWidth: string
  shadow: string
}

type ButtonStatusType = 'default' | 'hover' | 'click'
type ButtonOptionsType = 'background' | 'text' | 'border' | 'shadow'

interface ButtonsTheme {
  title: string
  description: string
  key: string
  default: ButtonStatusData
  hover: ButtonStatusData
  click: ButtonStatusData
  displayColorSection?: boolean
  activeTab?: ButtonStatusType
}

const GENERIC_SETTINGS: GenericSetting[] = [
  {
    key: 'config-button-border-radius',
    label: 'Border radius',
    type: 'pixel'
  },
  {
    key: 'config-button-border-width',
    label: 'Border width',
    type: 'pixel'
  },
  {
    key: 'config--main-button-base-font-weight',
    label: 'Font weight',
    type: 'input'
  },
  {
    key: 'config--main-button-base-texttransform',
    label: 'Uppercase',
    type: 'toggle'
  }
]

interface Props {
  updatedStyling: StylingConfigType
  defaultStyling: StylingConfigType
  onUpdate: (updatedStyling: StylingConfigType) => void
}

interface State {
  buttons: ButtonsTheme[]
  extendedButtonColors: null | ButtonsTheme[]
  enabledColorSelector: null | string
}

const BUTTON_STATUSES: ButtonStatusType[] = ['default', 'hover', 'click']
const BUTTON_COLOR_OPTIONS: ButtonOptionsType[] = ['background', 'text', 'border', 'shadow']

export default class Buttons extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      buttons: BUTTONS_CONFIGURATION,
      extendedButtonColors: null,
      enabledColorSelector: null
    }

    this.toggleColorSection = this.toggleColorSection.bind(this)
    this.resetButtons = this.resetButtons.bind(this)
    this.updateStyling = this.updateStyling.bind(this)
  }

  toggleColorSection(idx: number, button: ButtonsTheme) {
    const newButtons = this.state.buttons.concat([])
    newButtons[idx].displayColorSection = !button.displayColorSection
    newButtons[idx].activeTab = 'default'
    this.setState({ buttons: newButtons })
  }

  setActiveTab(idx: number, activeTab: ButtonStatusType) {
    const newButtons = this.state.buttons.concat([])
    newButtons[idx].activeTab = activeTab
    this.setState({ buttons: newButtons })
  }

  updateStyling(key: string, color: string) {
    const sameVariables: {
      [key: string]: string[]
    } = {
      'config-button-border-width': [
        'config-button-cta-border-width',
        'config-button-cta-hover-border-width',
        'config-button-cta-active-border-width',
        'config-button-secondary-border-width',
        'config-button-secondary-hover-border-width',
        'config-button-secondary-active-border-width',
        'config-button-toggle-border-width',
        'config-button-toggle-hover-border-width',
        'config-button-toggle-active-border-width',
        'config-button-toggle-on-border-width',
        'config-button-toggle-on-hover-border-width',
        'config-button-toggle-on-active-border-width',
        'config-button-cancel-border-width',
        'config-button-cancel-hover-border-width',
        'config-button-cancel-active-border-width'
      ],
      'config-button-border-radius': [
        'config-button-cta-border-radius',
        'config-button-cta-hover-border-radius',
        'config-button-cta-active-border-radius',
        'config-button-secondary-border-radius',
        'config-button-secondary-hover-border-radius',
        'config-button-secondary-active-border-radius',
        'config-button-toggle-border-radius',
        'config-button-toggle-hover-border-radius',
        'config-button-toggle-active-border-radius',
        'config-button-toggle-on-border-radius',
        'config-button-toggle-on-hover-border-radius',
        'config-button-toggle-on-active-border-radius',
        'config-button-cancel-border-radius',
        'config-button-cancel-hover-border-radius',
        'config-button-cancel-active-border-radius'
      ]
    }
    if (sameVariables[key]) {
      sameVariables[key].forEach((sameKey) => {
        this.props.updatedStyling[sameKey] = color
      })
    }
    this.props.onUpdate({ ...this.props.updatedStyling, [key]: color })
  }

  resetButtons() {
    this.props.onUpdate(this.props.defaultStyling)
  }

  render() {
    const { updatedStyling } = this.props
    const { buttons, enabledColorSelector } = this.state
    return (
      <div>
        <div className={classnames('box__pad', 'theme-configuration-generic-settings', 'with-border')}>
          {GENERIC_SETTINGS.map((genericButtonSetting: GenericSetting) => (
            <ConfigurationSetting label={genericButtonSetting.label} key={genericButtonSetting.key}>
              {genericButtonSetting.type === 'pixel' && (
                <div>
                  <InputGroup
                    title=''
                    type='number'
                    min={0}
                    onInput={(event) => this.updateStyling(genericButtonSetting.key, String(event.target.value) + 'px')}
                    value={String(updatedStyling[genericButtonSetting.key]).replace('px', '')}
                  />
                  <div>px</div>
                </div>
              )}
              {genericButtonSetting.type === 'input' && (
                <div>
                  <InputGroup
                    title=''
                    type='number'
                    min={0}
                    onInput={(event) => this.updateStyling(genericButtonSetting.key, event.target.value)}
                    value={String(updatedStyling[genericButtonSetting.key]).replace('px', '')}
                  />
                </div>
              )}
              {genericButtonSetting.type === 'toggle' && (
                <div>
                  <Switcher
                    on={updatedStyling[genericButtonSetting.key] === 'uppercase'}
                    onChange={() => {
                      if (updatedStyling[genericButtonSetting.key] === 'uppercase') {
                        return this.updateStyling(genericButtonSetting.key, 'uppercase')
                      }
                      return this.updateStyling(genericButtonSetting.key, 'none')
                    }}
                  />
                </div>
              )}
            </ConfigurationSetting>
          ))}
        </div>
        <div className='theme-configuration-list'>
          {buttons.map((button, idx) => {
            const activeTab = button.activeTab || 'default'
            return (
              <div
                key={button.key}
                className={classnames('box__pad', 'theme-configuration-list-item', generateCssClassName(button.title))}
              >
                <div className='theme-configuration-list-item_preview'>
                  <button className={`preview btn btn--${button.key}`}>{button.title}</button>
                </div>
                <div
                  className='theme-configuration-list-item_title'
                  onClick={() => this.toggleColorSection(idx, button)}
                >
                  <Icon size={24} color='#2B3346' icon={button.displayColorSection ? 'chevronDown' : 'chevronRight'} />
                  <span>{button.title}</span>
                </div>
                {button.displayColorSection ? (
                  <div className='theme-configuration-list-item_settings'>
                    <p className='theme-configuration-list-item_description'>{button.description}</p>
                    <div className='theme-configuration_tabs'>
                      {BUTTON_STATUSES.map((tab, index) => (
                        <div
                          key={index}
                          onClick={() => this.setActiveTab(idx, tab)}
                          className={classnames('theme-configuration_tab', {
                            'active-tab': button.activeTab === tab
                          })}
                        >
                          {tab}
                        </div>
                      ))}
                    </div>
                    {BUTTON_COLOR_OPTIONS.map((buttonColorOption, index) => {
                      const cssVariableForActiveTabState = button[activeTab][buttonColorOption]
                      if (buttonColorOption === 'shadow') {
                        return (
                          <div key={index} className='theme-configuration-list-item_setting'>
                            <label>
                              <div>
                                {buttonColorOption} &ndash; {activeTab}
                              </div>
                              <div className='theme-configuration-list-item_setting_subtext'>
                                Color x-axis y-axisradius, eg. rgba(0,0,0,0.2) 0 2px 4px
                              </div>
                            </label>
                            <InputGroup
                              title=''
                              type='text'
                              placeholder='rgba(0,0,0,0.2) 0 2px 4px'
                              onInput={(event) => this.updateStyling(cssVariableForActiveTabState, event.target.value)}
                              value={updatedStyling[cssVariableForActiveTabState] || ''}
                            />
                          </div>
                        )
                      }
                      return (
                        <div
                          key={index}
                          className={`theme-configuration-list-item_setting ${buttonColorOption}-${activeTab}`}
                        >
                          <label>
                            {buttonColorOption} Color &ndash; {activeTab}
                          </label>
                          <ColorPicker
                            color={updatedStyling[cssVariableForActiveTabState]}
                            disabled={false}
                            colorKey={cssVariableForActiveTabState}
                            disableColorSelector={() => this.setState({ enabledColorSelector: null })}
                            setColorSelector={() => {
                              const enabledColorSelector = cssVariableForActiveTabState
                              this.setState({ enabledColorSelector })
                            }}
                            updateColor={(color) => this.updateStyling(cssVariableForActiveTabState, color)}
                            isColorSelectorEnabled={enabledColorSelector === cssVariableForActiveTabState}
                          />
                        </div>
                      )
                    })}
                  </div>
                ) : null}
              </div>
            )
          })}
          <div className='box__pad'>
            <Button variant='warning' size='medium' onClick={this.resetButtons}>
              Reset to defaults
            </Button>
          </div>
        </div>
      </div>
    )
  }
}
